import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LifeBuoy, Plus, Paperclip, Loader2, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import AuthenticatedUploadImage from '@/components/AuthenticatedUploadImage';
import { suporteApi, uploadFile, parseUploadBlobIdFromFileUrl, getDisplayMessage, openUploadInNewTab } from '@/api/apiClient';
import { MODULOS_LISTA } from '@/lib/modulos';
import { resolveAllowedModulesForUser } from '@/lib/moduleAccess';
import { useAuth } from '@/lib/AuthContext';

const STATUS_OPTIONS = [
  { value: 'todos', label: 'Todos' },
  { value: 'aberto', label: 'Pendente' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'resolvido', label: 'Resolvido' },
  { value: 'fechado', label: 'Fechado' },
];

const PRIORIDADES = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'media', label: 'Média' },
  { value: 'alta', label: 'Alta' },
  { value: 'critica', label: 'Crítica' },
];

function prioridadeBadgeVariant(prioridade) {
  if (prioridade === 'critica') return 'destructive';
  if (prioridade === 'alta') return 'default';
  if (prioridade === 'media') return 'secondary';
  return 'outline';
}

function statusLabel(status) {
  const found = STATUS_OPTIONS.find((s) => s.value === status);
  if (found) return found.label;
  if (status === 'pendente') return 'Pendente';
  return status || '-';
}

export default function Suporte() {
  const queryClient = useQueryClient();
  const { user, empresa } = useAuth();
  const [statusFiltro, setStatusFiltro] = useState('todos');
  const [busca, setBusca] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [excluindoId, setExcluindoId] = useState('');
  const [ticketImagem, setTicketImagem] = useState(null);
  const [form, setForm] = useState({
    assunto: '',
    categoria_modulo: '',
    prioridade: 'media',
    descricao: '',
    arquivos: [],
  });

  const categoriaOptions = useMemo(() => {
    const { allowedModules } = resolveAllowedModulesForUser(user || {}, empresa || {});
    const allowed = Array.isArray(allowedModules) && allowedModules.length > 0
      ? allowedModules
      : MODULOS_LISTA.map((m) => m.id);
    return MODULOS_LISTA.filter((m) => allowed.includes(m.id));
  }, [user, empresa]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['suporte-tickets', statusFiltro, busca],
    queryFn: () => suporteApi.listar({
      status: statusFiltro === 'todos' ? '' : statusFiltro,
      q: busca.trim() || '',
      page: 1,
      limit: 50,
    }),
  });

  const items = Array.isArray(data?.items) ? data.items : [];
  const resumo = data?.resumo || { total: 0, aberto: 0, resolvido: 0 };

  const abrirTicket = async () => {
    if (!form.assunto.trim()) {
      toast.error('Informe o assunto.');
      return;
    }
    if (!form.categoria_modulo) {
      toast.error('Selecione uma categoria.');
      return;
    }
    if (!form.descricao.trim()) {
      toast.error('Descreva o problema.');
      return;
    }

    setSalvando(true);
    try {
      const arquivos = Array.isArray(form.arquivos) ? form.arquivos : [];
      const uploadBlobIds = [];
      for (const file of arquivos) {
        if (!String(file.type || '').toLowerCase().startsWith('image/')) {
          throw new Error(`Arquivo "${file.name}" não é imagem.`);
        }
        const up = await uploadFile(file);
        const blobId = parseUploadBlobIdFromFileUrl(up?.file_url);
        if (!blobId) throw new Error(`Falha ao anexar "${file.name}".`);
        uploadBlobIds.push(blobId);
      }

      await suporteApi.criar({
        assunto: form.assunto.trim(),
        categoria_modulo: form.categoria_modulo,
        prioridade: form.prioridade,
        descricao: form.descricao.trim(),
        upload_blob_ids: uploadBlobIds,
      });

      setDialogOpen(false);
      setForm({
        assunto: '',
        categoria_modulo: '',
        prioridade: 'media',
        descricao: '',
        arquivos: [],
      });
      toast.success('Ticket enviado com sucesso.');
      queryClient.invalidateQueries({ queryKey: ['suporte-tickets'] });
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Não foi possível abrir o ticket.'));
    } finally {
      setSalvando(false);
    }
  };

  const excluirTicket = async (ticketId) => {
    if (!ticketId) return;
    const ok = window.confirm('Deseja realmente excluir este chamado de suporte?');
    if (!ok) return;
    setExcluindoId(ticketId);
    try {
      await suporteApi.excluir(ticketId);
      toast.success('Chamado excluído com sucesso.');
      queryClient.invalidateQueries({ queryKey: ['suporte-tickets'] });
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Não foi possível excluir o chamado.'));
    } finally {
      setExcluindoId('');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Suporte</h1>
          <p className="text-slate-600">Abra chamados e acompanhe o andamento</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Abrir ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Novo ticket</DialogTitle>
              <DialogDescription>
                Preencha os campos abaixo para abrir um chamado.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Assunto</Label>
                <Input
                  value={form.assunto}
                  onChange={(e) => setForm((f) => ({ ...f, assunto: e.target.value }))}
                  placeholder="Ex.: Erro ao gerar PDF do contrato"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select
                    value={form.categoria_modulo || ''}
                    onValueChange={(v) => setForm((f) => ({ ...f, categoria_modulo: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o módulo" />
                    </SelectTrigger>
                    <SelectContent>
                      {categoriaOptions.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Prioridade</Label>
                  <Select
                    value={form.prioridade}
                    onValueChange={(v) => setForm((f) => ({ ...f, prioridade: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORIDADES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea
                  className="min-h-32"
                  maxLength={1000}
                  value={form.descricao}
                  onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                  placeholder="Descreva em detalhes o problema, passos para reproduzir e mensagens de erro."
                />
                <p className="text-xs text-slate-500 text-right">{form.descricao.length}/1000</p>
              </div>
              <div className="space-y-2">
                <Label>Anexos de imagem</Label>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []).slice(0, 6);
                    setForm((f) => ({ ...f, arquivos: files }));
                  }}
                />
                {form.arquivos.length > 0 ? (
                  <ul className="text-xs text-slate-600 space-y-1">
                    {form.arquivos.map((f) => (
                      <li key={f.name} className="flex items-center gap-2">
                        <Paperclip className="h-3 w-3" />
                        {f.name}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <div className="flex justify-end">
                <Button onClick={abrirTicket} disabled={salvando} className="gap-2">
                  {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Enviar ticket
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total de tickets</p>
              <p className="text-2xl font-bold">{resumo.total || 0}</p>
            </div>
            <LifeBuoy className="h-6 w-6 text-blue-500" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Em aberto</p>
            <p className="text-2xl font-bold">{resumo.aberto || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Resolvidos</p>
            <p className="text-2xl font-bold">{resumo.resolvido || 0}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Tickets de suporte</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_OPTIONS.map((s) => (
              <Button
                key={s.value}
                variant={statusFiltro === s.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFiltro(s.value)}
              >
                {s.label}
              </Button>
            ))}
            <div className="ml-auto w-full sm:w-72">
              <Input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por assunto/categoria"
              />
            </div>
          </div>

          {isLoading || isFetching ? (
            <div className="py-10 text-center text-slate-500">Carregando tickets...</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-slate-500">Nenhum ticket encontrado.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="py-2 pr-3">ID</th>
                    <th className="py-2 pr-3">Assunto</th>
                    <th className="py-2 pr-3">Categoria</th>
                    <th className="py-2 pr-3">Prioridade</th>
                    <th className="py-2 pr-3">Data</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Status da resolução/fechamento</th>
                    <th className="py-2">Anexos</th>
                    <th className="py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((t) => (
                    <tr key={t.id} className="border-b last:border-0">
                      <td className="py-3 pr-3 font-mono text-xs">{t.id.slice(0, 8)}</td>
                      <td className="py-3 pr-3">{t.assunto}</td>
                      <td className="py-3 pr-3">{t.categoria_modulo}</td>
                      <td className="py-3 pr-3">
                        <Badge variant={prioridadeBadgeVariant(t.prioridade)}>
                          {PRIORIDADES.find((p) => p.value === t.prioridade)?.label || t.prioridade}
                        </Badge>
                      </td>
                      <td className="py-3 pr-3">{new Date(t.created_at).toLocaleDateString('pt-BR')}</td>
                      <td className="py-3 pr-3">{statusLabel(t.status)}</td>
                      <td className="py-3 pr-3 max-w-[28rem]">
                        {t.status === 'resolvido' || t.status === 'fechado' ? (
                          <span className="text-slate-700 whitespace-pre-wrap">
                            {t.status_resolucao?.trim() || 'Chamado encerrado sem detalhamento informado.'}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3">
                        {Array.isArray(t.anexos) && t.anexos.length > 0 ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            onClick={() => setTicketImagem(t)}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            Ver ({t.anexos.length})
                          </Button>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => excluirTicket(t.id)}
                          disabled={excluindoId === t.id}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          {excluindoId === t.id ? 'Excluindo...' : 'Excluir'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!ticketImagem} onOpenChange={(open) => !open && setTicketImagem(null)}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Imagens anexadas</DialogTitle>
            <DialogDescription>
              {ticketImagem?.assunto || 'Ticket'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[65vh] overflow-auto pr-1">
            {(ticketImagem?.anexos || []).map((a) => (
              <button
                key={a.id}
                type="button"
                className="text-left border rounded-md p-2 hover:bg-slate-50"
                onClick={() => openUploadInNewTab(a.file_url)}
              >
                <AuthenticatedUploadImage
                  src={a.file_url}
                  alt={a.nome_arquivo}
                  className="w-full h-40 object-cover rounded"
                />
                <p className="mt-2 text-xs text-slate-600 truncate">{a.nome_arquivo}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
